import moment from 'moment';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {AppDatePicker, Col, Row, UiInput, UiQuestionnaireLayout, UiText} from '~/components/elements';
import {PAGE_URLS} from '~/constants';
import {WEIGHT_LOSS_RENEWAL_KEYS, WEIGHT_LOSS_RENEWAL_QUESTIONNAIRES} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {DATE_MEASUREMENT_FORMAT, DATE_OF_BIRTH_TIME_FORMAT, formatDate} from '~/utils/helpers';

interface Props {}

const MDQRenewalQuestionnaire2Screen = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const mdq = useSelector((state: RootState) => state.questionnaires);
  const {methodKey, medicalUUID, answerOrder} = mdq;

  const collectedData = mdq.collectedData;
  //   const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  //   const {wpAuthUser} = medicalQuestionnaire;

  //   const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = WEIGHT_LOSS_RENEWAL_QUESTIONNAIRES.find(
    i => i.key === WEIGHT_LOSS_RENEWAL_KEYS.ADDITIONAL_INFORMATION,
  );

  const currentAnswer = useSelector(
    (state: RootState) => state.questionnaires?.collectedData[WEIGHT_LOSS_RENEWAL_KEYS.ADDITIONAL_INFORMATION]?.data,
  );

  const {inputs, setInputs} = useInput({
    height: currentAnswer?.height || '',
    weight: currentAnswer?.weight || '',
    bmi: currentAnswer?.bmi || '',
    dateOfMeasurement: currentAnswer?.dateOfMeasurement || '',
  });

  const isInputsFilled = inputs.height && inputs.weight;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    const onlyNumbersAndOneDotRegex = /^\d*\.?\d*$/;
    if (onlyNumbersAndOneDotRegex.test(value)) {
      setInputs({[name]: value});
    }
  };

  const calculateBmi = () => {
    return Number((inputs.weight / Math.pow(inputs.height / 100, 2)).toFixed(1));
  };

  const onClickSubmit = async () => {
    const userBmi = calculateBmi();

    const answerData = {
      key: WEIGHT_LOSS_RENEWAL_KEYS.ADDITIONAL_INFORMATION,
      index: questionnaire?.index || '',
      data: {
        height: Number(inputs?.height),
        weight: Number(inputs?.weight),
        bmi: userBmi,
        dateOfMeasurement: inputs?.dateOfMeasurement,
      },
      order: answerOrder + 1,
    };

    dispatch(setQuestionnaireAnswer(answerData));
    dispatch(
      submitOrLogMdq({
        data: {
          uid: medicalUUID,
          methodKey: methodKey.toLowerCase(),
          status: 'draft',
          data: {
            ...collectedData,
            [WEIGHT_LOSS_RENEWAL_KEYS.ADDITIONAL_INFORMATION]: answerData,
          },
        },
      }),
    );
    history.push(PAGE_URLS.MDQ_RENEWAL_DID_MEDICATION_WORK + window.location.search);
  };

  const renderBmi = () => {
    if (isInputsFilled) {
      return (
        <UiText ml={2} fontWeight="bold" fontSize="xl">
          {calculateBmi()}
        </UiText>
      );
    }
    return null;
  };

  return (
    <UiQuestionnaireLayout
      backIcon
      actions={questionnaire?.actions}
      disableNext={!isInputsFilled || !inputs.dateOfMeasurement}
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
      pageTitle={questionnaire?.title}
    >
      <Col alignItems="center" textAlign="center" mt={14} px={2}>
        <UiText fontSize="2xl" fontWeight="bold" mb={4}>
          {questionnaire?.questions?.default}
        </UiText>
        <UiText fontSize="sm" mb={14} maxW={80}>
          {questionnaire?.questions?.sub}
        </UiText>
        <UiText fontSize="xl" fontWeight="bold" mb={14}>
          {questionnaire?.questions?.sub2}
        </UiText>
        <Row style={{alignItems: 'flex-end'}} mb={12}>
          <UiInput
            name="height"
            type="text"
            style={{
              border: 'none',
              outline: 'none',
              borderBottom: inputs.height ? 'none' : '2px solid gray',
              maxWidth: '4rem',
              height: '1.5rem',
              padding: 0,
            }}
            fontSize="md"
            color="black"
            fontWeight="600"
            opacity={0.4}
            value={inputs.height}
            onChange={handleInputChange}
          />
          <UiText fontSize="md" color="black" fontWeight="600" opacity={0.4} ml={5}>
            cm
          </UiText>
        </Row>
        <UiText fontSize="xl" fontWeight="bold" mb={14}>
          {questionnaire?.questions?.sub3}
        </UiText>
        <Row style={{alignItems: 'flex-end'}} mb={12}>
          <UiInput
            name="weight"
            type="text"
            style={{
              border: 'none',
              outline: 'none',
              borderBottom: inputs.weight ? 'none' : '2px solid gray',
              maxWidth: '4rem',
              height: '1.5rem',
              padding: 0,
            }}
            fontSize="md"
            color="black"
            fontWeight="600"
            opacity={0.4}
            value={inputs.weight}
            onChange={handleInputChange}
          />
          <UiText fontSize="md" color="black" fontWeight="600" opacity={0.4} ml={5}>
            kg
          </UiText>
        </Row>
        <Row style={{alignItems: 'center'}} mb={10} mr={isInputsFilled ? 0 : '10%'}>
          <UiText fontSize="xl" color="black">
            Your BMI is
          </UiText>
          {renderBmi()}
        </Row>
        <UiText fontSize="xl" fontWeight="bold" mb={8}>
          {questionnaire?.questions?.sub4}
        </UiText>
        <AppDatePicker
          selected={!!inputs.dateOfMeasurement ? moment(inputs?.dateOfMeasurement) : undefined}
          onChange={value => {
            setInputs({dateOfMeasurement: formatDate(value)});
          }}
          // peekNextMonth
          showMonthDropdown
          showYearDropdown
          todayButton={undefined}
          dropdownMode="select"
          customInput={
            <UiInput
              style={{
                border: 'none',
                outline: 'none',
                borderBottom: inputs.dateOfMeasurement ? 'none' : '2px solid gray',
                maxWidth: inputs.dateOfMeasurement ? '16rem' : '6rem',
                height: '1.5rem',
                padding: 0,
              }}
              textAlign="center"
              fontSize="md"
              color="black"
              fontWeight="600"
              opacity={0.4}
            />
          }
          dateFormat={DATE_MEASUREMENT_FORMAT}
          maxDate={moment().toDate()}
          disabledKeyboardNavigation
          popperPlacement="bottom"
        />
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default MDQRenewalQuestionnaire2Screen;
